// Fires a short-lived `predicate.display.fallback.<rule>` QPL flow
// whenever a display-rule consumer falls back to its imperative
// reference path. Reasons:
//
//   "cold_start"  — rule cache hasn't synced yet (first launch /
//                   post-sign-out window). Expected at startup;
//                   sustained signal would indicate the sync
//                   handler isn't running.
//   "walker_trap" — walker threw on this row. The walker error
//                   itself is already reported via QPLErrorReporter;
//                   this counter quantifies the imperative-served
//                   subset of renders so dashboards can compute
//                   "% of renders served by walker" per rule.
//   "empty"       — walker returned an empty FormattedString when
//                   the consumer expected something to show. Hints
//                   at a rule body that took an unexpected branch.
//
// Spec: chores docs/plans/elka-display-followups.md item 2-followup
// (post-migration observability).

import { startFlow, emitPoint, finalizeFlow } from '@/features/qpl-client';

export type FallbackReason = 'cold_start' | 'walker_trap' | 'empty';

export function emitDisplayFallback(
  ruleID: string,
  reason: FallbackReason,
  rowKey?: string,
): void {
  const handle = startFlow(`predicate.display.fallback.${ruleID}`, {
    rule_id: ruleID,
    reason,
    row_key: rowKey ?? '',
  });
  emitPoint(handle.id, 'fallback', {
    rule_id: ruleID,
    reason,
    row_key: rowKey ?? '',
  });
  // Outcome=SUCCESS — fallback isn't an error, just instrumentation.
  // The walker_trap case is already FAILED via the error reporter.
  finalizeFlow(handle.id, 'SUCCESS');
}
